import React from 'react';

import classNames from 'classnames';
import { MessageSquarePlus } from 'lucide-react';

import { accountNameOf } from '../accountName';
import { RATING_FACES, RatingFace } from '../ratings';
import { Conversation, ConversationState } from '../types';

export const relativeTime = (iso: string): string => {
  const minutes = Math.floor((Date.now() - new Date(iso).getTime()) / 60_000);
  if (minutes < 1) return 'now';
  if (minutes < 60) return `${minutes}m`;

  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h`;
  return `${Math.floor(hours / 24)}d`;
};

type Props = {
  conversations: Conversation[];
  selectedId: string | null;
  state: ConversationState;
  isLoading: boolean;
  search: string;
  onSearchChange: (value: string) => void;
  onSelect: (id: string) => void;
  onStateChange: (state: ConversationState) => void;
  onNewConversation: () => void;
};

// Telescope's ThreadList look: grey column (white-1.0), tabs with an
// underline, rows that round their left corners and go white when selected.
const ConversationList: React.FC<Props> = ({
  conversations,
  selectedId,
  state,
  isLoading,
  search,
  onSearchChange,
  onSelect,
  onStateChange,
  onNewConversation,
}: Props) => {
  // Same reading as the fetch in index: blanks are not a search.
  const isSearching = search.trim() !== '';

  return (
    <div className="flex flex-col w-full h-full bg-white-1.0 border-r border-white-2.0 md:w-1/3 md:flex-shrink-0">
      <div className="flex items-center gap-2 px-4 py-2 bg-white-0.0 border-b border-white-2.0">
        <label className="sr-only" htmlFor="support-search">
          Search conversations
        </label>
        <input
          id="support-search"
          type="search"
          value={search}
          onChange={(event) => onSearchChange(event.target.value)}
          placeholder="Search by user or account…"
          className="w-full px-3 py-1.5 text-sm rounded bg-white-1.0 text-black-1.0 focus:outline-none"
        />
        <button
          type="button"
          aria-label="New conversation"
          title="New conversation"
          className="flex-shrink-0 p-1.5 rounded text-primary-2.0 hover:bg-white-1.0"
          onClick={onNewConversation}
        >
          <MessageSquarePlus size={18} />
        </button>
      </div>
      <div
        className="flex gap-4 px-4 bg-white-0.0 border-b border-white-2.0"
        role="tablist"
        aria-label="Conversation status"
      >
        {(['open', 'closed'] as ConversationState[]).map((option) => (
          <button
            key={option}
            type="button"
            role="tab"
            aria-selected={state === option}
            onClick={() => onStateChange(option)}
            className={classNames(
              'py-3 text-sm font-medium capitalize border-b-4 transition ease-in-out duration-200',
              state === option
                ? 'border-primary-2.0 text-primary-2.0'
                : 'border-white-0.0 text-gray-2.0 hover:text-primary-2.0'
            )}
          >
            {option}
            {state === option && !isSearching && (
              <span
                aria-hidden="true"
                className="ml-2 px-1 text-xs rounded bg-primary-2.0 text-white-0.0"
              >
                {conversations.length}
              </span>
            )}
          </button>
        ))}
      </div>

      <ul className="flex-1 pt-2 pl-2 overflow-y-auto" aria-live="polite">
        {isLoading && <li className="p-4 text-sm text-gray-1.0">Loading…</li>}

        {!isLoading && conversations.length === 0 && (
          <li className="p-4 text-sm text-gray-1.0">Nothing here.</li>
        )}

        {conversations.map((conversation) => {
          const accountName = accountNameOf(conversation.contact);
          // The open thread is being read, whatever the list last heard.
          const isUnread = conversation.unread && selectedId !== conversation.id;
          // Bold is "still owes an answer": it stays while the customer has the
          // last word, read or not, and only a reply clears it. The dot is
          // what reading clears.
          const isBold = conversation.awaiting_reply;
          const face = conversation.rating ? RATING_FACES[conversation.rating] : undefined;
          return (
            <li key={conversation.id}>
              <button
                type="button"
                onClick={() => onSelect(conversation.id)}
                className={classNames(
                  'w-full px-4 py-2 text-left rounded-tl rounded-bl transition-colors',
                  selectedId === conversation.id ? 'bg-white-0.0' : 'hover:bg-white-2.0'
                )}
              >
                <div className="flex items-baseline justify-between gap-2">
                  <span
                    className={classNames(
                      'flex items-center gap-2 text-sm truncate text-black-1.0',
                      isBold ? 'font-bold' : 'font-semibold'
                    )}
                  >
                    {isUnread && (
                      <span
                        aria-hidden="true"
                        className="flex-shrink-0 w-2 h-2 rounded-full bg-primary-2.0"
                      />
                    )}
                    <span className="min-w-0 truncate">
                      {conversation.contact.name || conversation.contact.email || 'Unknown'}
                    </span>
                    {isUnread && <span className="sr-only">(new message)</span>}
                    {!isUnread && conversation.awaiting_reply && (
                      <span className="sr-only">(awaiting reply)</span>
                    )}
                  </span>
                  <span className="flex items-baseline flex-shrink-0 gap-1">
                    {/* Search results span both tabs, so a row from the other side
                    says which one it came from. */}
                    {isSearching && conversation.state === 'closed' && (
                      <span className="px-1.5 text-xs rounded-full bg-white-2.0 text-gray-1.0">
                        closed
                      </span>
                    )}
                    {face && (
                      <span role="img" aria-label={`Rated ${face.label}`} className="self-center">
                        <RatingFace rating={conversation.rating ?? 0} size={18} labelled={false} />
                      </span>
                    )}
                    <span className="text-xs text-gray-1.0">
                      {relativeTime(conversation.last_message_at)}
                    </span>
                  </span>
                </div>
                {accountName && <p className="text-xs truncate text-gray-1.0">{accountName}</p>}
                <p
                  className={classNames(
                    'mt-1 text-sm truncate',
                    isBold ? 'font-semibold text-black-1.0' : 'text-gray-1.0'
                  )}
                >
                  {conversation.preview || 'No messages'}
                </p>
                {conversation.labels.length > 0 && (
                  <div className="flex flex-wrap gap-1 mt-1">
                    {conversation.labels.map((label) => (
                      <span
                        key={label.id}
                        className="px-1.5 py-0.5 text-xs rounded-full bg-white-2.0 text-gray-3.0"
                      >
                        {label.name}
                      </span>
                    ))}
                  </div>
                )}
              </button>
            </li>
          );
        })}
      </ul>
    </div>
  );
};

export default ConversationList;
